import { useState, type ReactNode } from 'react'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import MenuOutlined from '@mui/icons-material/MenuOutlined'
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { NavLink, useLocation } from 'react-router-dom'
import { nomeParaTela, type Me, type TenantBrand, type TenantOption } from '../api/session'
import { visibleNavItems } from './navItems'
import { t } from '../i18n'
import { brand } from '../theme'

/**
 * B1 (sessao 35) - casca do aplicativo no padrao Rippling (S31-7, regua T11):
 * menu lateral FIXO a esquerda, conteudo a direita, barra fina no topo com o
 * usuario e "Sair". Substitui a barra de topo do B0 (`AppHeader`, julho).
 *
 * MARCA DO CLIENTE (S31-10): cartao branco no topo do menu com o logo do cliente
 * (so PNG/WebP https, validado em `api/session.ts`) ou o NOME dele; embaixo, a
 * FAIXA FINA na cor dele. A cor do cliente aparece SO ali -- nunca em botao ou
 * texto. Sem cor valida, nao ha faixa.
 *
 * PAR FUNDO <-> LOGO (herdado do AppHeader.test, D3): o menu e `brand.navy`
 * (= theme primary) e por isso usa a logo NEGATIVA do STRONGERH ONE e da GENTE.
 *
 * MENU POR PERMISSAO (T4): o item aparece so se o `/me` trouxer a permissao que
 * a rota da API exige. Esconder e conveniencia; autorizar e do backend.
 *
 * T12: copia-se o PADRAO de uso, nunca o visual de outra marca; cores e fontes
 * sao da paleta Gente v4.0 (tema).
 *
 * RESPONSIVA (sessao 39, S38-A4 / S39-1..4; ADR-0024 nao-escopo: "a SPA e
 * responsiva"): abaixo de 900 px (`md` do MUI) o menu sai da tela e vira GAVETA
 * temporaria, aberta pelo botao "Abrir menu" na barra; leva o MESMO conteudo do
 * menu fixo (logo, cartao do cliente, itens por permissao, assinatura GENTE) e
 * fecha ao escolher um item, no Esc, no fundo escuro e no botao "Fechar menu".
 * Sem `matchMedia` (jsdom) a casca e LARGA -- o comportamento de antes.
 *
 * s67 / ADR-0033 2i (Marcelo 25/10/2026 14:46, "APROVADO 2i"): tres encaixes OPCIONAIS, preenchidos pelo RequireSession
 * (que tem o QueryClient; a casca nao le dado nenhum): `assistantLabel` troca o rotulo do item do assistente ("Pergunte a
 * VIGA" com a IA ligada -- V1); `search` vai na barra de cima (busca no topo, padrao Rippling -- R2); `aside` e a coluna
 * a direita do conteudo (o painel da VIGA, padrao Attio -- V3). Sem eles, a casca e a de antes.
 */

export const SHELL_MENU_WIDTH = 248
export const SHELL_PRODUCT_LOGO_SRC = '/brand/strongerh-one-horizontal-chapada-negativa.svg'
export const SHELL_GENTE_LOGO_SRC = '/gente/logo-horizontal-negativo.svg'
export const SHELL_GENTE_LOGO_HEIGHT = 32
export const SHELL_STRIPE_HEIGHT = 4
/** Fundo do menu: o proprio primary do tema (escuro -> logos negativas). */
export const SHELL_MENU_BACKGROUND = brand.navy
/** id da navegacao, alvo do `aria-controls` do botao "Abrir menu". */
export const SHELL_NAV_ID = 'menu-principal'

/**
 * s54 (item 2 fase 1): o administrador da Gente escolhe a EMPRESA em que trabalha. So vem preenchido para quem
 * tem `tenant.list`; a faixa aparece enquanto ele estiver numa empresa que nao e a dele.
 */
export interface WorkArea {
  options: TenantOption[]
  ownTenantId: string
  currentTenantId: string
  currentName: string
  onChange: (tenantId: string | null) => void
}

function ClientCard({ tenant }: { tenant: TenantBrand | null }) {
  return (
    <Box
      data-testid="client-card"
      sx={{ mx: 2, mb: 2, bgcolor: brand.white, borderRadius: 1, overflow: 'hidden' }}
    >
      {/* s68 / LC (Marcelo 26/10/2026 18:16, "enquadra os logos certinho... centralizado"): o logo fica CENTRALIZADO no
          cartao, numa caixa de altura FIXA (72 px), e cresce ate 52 px de altura sem passar da largura -- logos de
          proporcoes diferentes (LUTEPEL largo, TERRASEMEN alto) ocupam o cartao de forma parecida. */}
      <Box
        data-testid="client-card-frame"
        sx={{ px: 2, py: 1, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {tenant?.logoUrl ? (
          <Box
            component="img"
            src={tenant.logoUrl}
            alt={`${t.shell.clientLogoAlt} ${tenant.displayName}`}
            sx={{ maxHeight: 52, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <Typography variant="subtitle2" component="span" color="primary" noWrap>
            {tenant?.displayName ?? ''}
          </Typography>
        )}
      </Box>
      {tenant?.stripeColor && (
        <Box
          data-testid="client-stripe"
          aria-hidden="true"
          sx={{ height: SHELL_STRIPE_HEIGHT, bgcolor: tenant.stripeColor }}
        />
      )}
    </Box>
  )
}

function MenuContent({
  me,
  tenant,
  pathname,
  onNavigate,
  onClose,
  assistantLabel,
}: {
  me: Me
  tenant: TenantBrand | null
  pathname: string
  onNavigate?: () => void
  onClose?: () => void
  assistantLabel?: string
}) {
  const items = visibleNavItems(me.permissions)
  return (
    <>
      <Box sx={{ px: 2, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          component="img"
          src={SHELL_PRODUCT_LOGO_SRC}
          alt={t.app.name}
          sx={{ height: 28, width: 'auto', display: 'block' }}
        />
        {onClose && (
          <IconButton aria-label={t.shell.closeMenu} onClick={onClose} sx={{ color: brand.white }} edge="end">
            <CloseOutlined />
          </IconButton>
        )}
      </Box>
      <ClientCard tenant={tenant} />
      <List sx={{ px: 1, flexGrow: 1 }}>
        {items.map((item) => {
          const selected = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)
          return (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              selected={selected}
              onClick={onNavigate}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                color: brand.white,
                '&.Mui-selected, &.Mui-selected:hover': { bgcolor: brand.navyLight },
                '&:hover': { bgcolor: brand.navyDark },
              }}
            >
              <ListItemIcon sx={{ color: brand.white, minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.to === '/assistente' && assistantLabel ? assistantLabel : item.label} />
            </ListItemButton>
          )
        })}
      </List>
      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="caption" component="p" sx={{ color: brand.grey, mb: 0.5 }}>
          {t.shell.signature}
        </Typography>
        <Box
          component="img"
          src={SHELL_GENTE_LOGO_SRC}
          alt={t.login.brandSignatureAlt}
          sx={{ height: SHELL_GENTE_LOGO_HEIGHT, width: 'auto', display: 'block' }}
        />
      </Box>
    </>
  )
}

const NAV_SX = {
  width: SHELL_MENU_WIDTH,
  bgcolor: SHELL_MENU_BACKGROUND,
  color: brand.white,
  display: 'flex',
  flexDirection: 'column',
} as const

export function AppShell({
  me,
  tenant,
  onSignOut,
  children,
  workArea,
  assistantLabel,
  search,
  aside,
}: {
  me: Me
  tenant: TenantBrand | null
  onSignOut: () => void
  children: ReactNode
  workArea?: WorkArea
  assistantLabel?: string
  search?: ReactNode
  aside?: ReactNode
}) {
  const { pathname } = useLocation()
  const theme = useTheme()
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeDrawer = () => setDrawerOpen(false)

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isNarrow ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={closeDrawer}
          PaperProps={{ sx: { bgcolor: SHELL_MENU_BACKGROUND } }}
        >
          <Box component="nav" id={SHELL_NAV_ID} aria-label={t.shell.navLabel} sx={{ ...NAV_SX, minHeight: '100%' }}>
            <MenuContent
              me={me}
              tenant={tenant}
              pathname={pathname}
              onNavigate={closeDrawer}
              onClose={closeDrawer}
              assistantLabel={assistantLabel}
            />
          </Box>
        </Drawer>
      ) : (
        <Box
          component="nav"
          id={SHELL_NAV_ID}
          aria-label={t.shell.navLabel}
          sx={{ ...NAV_SX, flexShrink: 0, position: 'sticky', top: 0, height: '100vh' }}
        >
          <MenuContent me={me} tenant={tenant} pathname={pathname} assistantLabel={assistantLabel} />
        </Box>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="static" color="inherit" elevation={0} component="header">
          <Toolbar sx={{ justifyContent: 'flex-end', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
            {isNarrow && (
              <IconButton
                aria-label={t.shell.openMenu}
                aria-expanded={drawerOpen}
                aria-controls={SHELL_NAV_ID}
                onClick={() => setDrawerOpen(true)}
                color="primary"
                edge="start"
                sx={{ mr: 'auto' }}
              >
                <MenuOutlined />
              </IconButton>
            )}
            {search && <Box sx={{ display: 'flex', flex: '1 1 auto', minWidth: 0, mr: 'auto' }}>{search}</Box>}
            {workArea && (
              <TextField
                select
                size="small"
                id="work-tenant"
                label={t.shell.workTenantLabel}
                value={workArea.currentTenantId}
                onChange={(e) => workArea.onChange(e.target.value === workArea.ownTenantId ? null : e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 200, mr: isNarrow || search ? 0 : 'auto' }}
              >
                {!workArea.options.some((o) => o.id === workArea.ownTenantId) && (
                  <option value={workArea.ownTenantId}>{t.shell.workTenantOwn}</option>
                )}
                {workArea.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.displayName}
                  </option>
                ))}
              </TextField>
            )}
            <Typography variant="body2" color="text.secondary" noWrap>
              {nomeParaTela(me.user?.username ?? '')}
            </Typography>
            <Button variant="text" color="primary" onClick={onSignOut}>
              {t.shell.signOut}
            </Button>
          </Toolbar>
        </AppBar>
        {workArea && workArea.currentTenantId !== workArea.ownTenantId && (
          <Box
            data-testid="work-tenant-banner"
            role="status"
            // s55b (pedido de Marcelo): cor do STRONGERH (azul-escuro, 15:1 com branco) + acento laranja da marca;
            // mais baixa e numa linha so. O acento mantem o alerta: a faixa existe para ninguem salvar na empresa errada.
            sx={{
              px: { xs: 2, sm: 3 },
              py: 0.25,
              bgcolor: brand.navy,
              color: brand.white,
              borderBottom: `3px solid ${brand.orange}`,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
              {t.shell.workTenantBanner} {workArea.currentName}
            </Typography>
            <Button
              size="small"
              variant="text"
              color="inherit"
              sx={{ py: 0, minHeight: 0, fontSize: '0.8125rem', textDecoration: 'underline', textTransform: 'none' }}
              onClick={() => workArea.onChange(null)}
            >
              {t.shell.workTenantBack}
            </Button>
          </Box>
        )}
        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
      {aside}
    </Box>
  )
}
